<template>
  <table class="table" v-if="timeSchedule">
    <thead>
      <tr>
        <th v-for="(item, index) in days" :key="item + index" :class="{'today': isToday(index), [getDayFullType(index)]: true}">
          <div class="table__cell-inner">
            <div class="table__cell-warn" v-if="isDayFullMsg(index)">{{isDayFullMsg(index)}}</div>
            <div class="table__cell table__head">{{item}}, {{index + 1}}</div>
          </div>
        </th>
      </tr>
      <tr>
        <td v-for="(item, i) in timeSchedule" :key="`${item.start}-${i}`"
            :class="{'today': isToday(i)}">
          <div class="table__cell-sub table__cell table__cell-dayoff" v-if="isDayOff(item)" :class="{'today': isToday(i)}">
            <div class="table__cell-time">Выходной</div>
          </div>
          <div class="table__cell-sub table__cell" v-else>
            <div class="table__cell-time">
              <span>{{item.start}}</span>
              <template v-if="item.end !== '--:--'">
                <vita-icon class="table__cell-icon" name="arrow-sm"/>
                <span>{{item.end}}</span>
              </template>
            </div>
          </div>
        </td>
      </tr>
    </thead>
  </table>
</template>

<script lang="ts" setup>
import {useStore} from "vuex";
import {computed} from "vue";
import { Working, WTime } from '@/types';
import { State } from "@/store/user/types";
import { addDays, parse } from 'date-fns';
import { ru } from 'date-fns/locale';
import { extractDatePartsGURV } from '@/utils/gurv';

const store = useStore<State>()
const days = computed<string[]>(() => {
  return store.getters['getCurrentDays']
})

const currentMonth = computed<number>(() => {
  return store.getters['getCurrentMonthNumber']
})

// Вычисляемое свойство для сортировки расписания
const sortedTimeScheduleData = computed(() => {
  const timeScheduleData = store.getters['data/timeSchedule'] as WTime[];
  if (!timeScheduleData) return null;

  return Object.entries(timeScheduleData).sort(([a], [b]) => {
    const dayA = parseInt(a.split('.')[0], 10);
    const dayB = parseInt(b.split('.')[0], 10);
    return dayA - dayB;
  });
});

// Проверка на сегодняшний день
const isToday = (index: number) => {
  // Добавлена проверка из-за ошибки с датами после logout
  if (!currentMonth.value) return false

  const today = new Date();
  const actualMonth = (today.getMonth() + 1).toString()
  const month = currentMonth.value.toString();

  return today.getDate() === (index + 1) && actualMonth === month
}

const isOfflineDirectorRole = computed<boolean>(() => {
  return store.getters['getOfflineDirectorRole']
})

const isWFMReaderRole = computed<boolean>(() => {
  return store.getters['getWFMReaderRole']
})

const isGurvRole = computed<boolean>(() => {
  return store.getters['getGurvRole']
})

const frvDedicatedHours = computed<number>(() => {
  return store.getters['data/coreStaff'] && store.getters['data/coreStaff'].dedicated
})

const isPrevMonthActive = computed<boolean>(() => {
  return !!localStorage.getItem('activePrevMonth')
})

// Предупреждения о том, что часы заполнены некорректно
const isDayFullMsg = (index: number) => {
  const dayFullType = getDayFullType(index);

  // Выбранный месяц не прошедший, в этом месяце есть frvMainPlan и на этом дне "dayFull": "less"
  if (!isPrevMonthActive.value && (frvDedicatedHours.value > 0 && dayFullType === 'less')) {
    return 'Добавьте смены на день'
  }
  // В остальных случаях проверяем только "dayFull"
  return dayFullType === 'more' ? 'Сократите часы смен' : '';
}

// Определение статуса дня
const getDayFullType = (index: number): string => {
  const sortedData = sortedTimeScheduleData.value;
  // Убрано отображение для WFM_READER (OMR-3070)
  if ((isWFMReaderRole.value || isOfflineDirectorRole.value) || !sortedData || !sortedData[index]) return '';

  const parsedDate = parse(sortedTimeScheduleData.value[index][0], "dd.MM.yyyy", new Date(), { locale: ru });

  // Игнорируем статус дня т.к. 2 и 17 числа можно создавать смены только на вчера (OMR-3438)
  const { creatingDay, creatingMonth, currentMonthNumber, currentDay } = extractDatePartsGURV(sortedTimeScheduleData.value[index][0]);
  const isSalaryDays = currentDay >= 17 && (creatingDay === 15 && creatingMonth === currentMonthNumber)


  if (isGurvRole.value) {
    return sortedData[index][1].dayFull || '';
  }

  if (!isSalaryDays && (addDays(new Date(), -3).getTime() < parsedDate.getTime())) {
    return sortedData[index][1].dayFull || '';
  }

  return ''
};

// Проверка на выходной день
const isDayOff = (item: Working): boolean => item.start === '00:00' && item.end === '00:00';

const timeSchedule = computed<Working[] | null>(() => {
  if (!sortedTimeScheduleData.value) return null;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return sortedTimeScheduleData.value.map(([_, item]) => {
    const typedItem = item as WTime;
    if (typedItem.actualWorkTime && !typedItem.emptySchedule) {
      const [start, end] = typedItem.actualWorkTime.split('-')
      const dayFull = typedItem.dayFull || ''
      return { start, end, dayFull }

    } else if (typedItem.emptySchedule) {
      // Расписание отсутствует
      return { start: '--:--', end: '--:--' }
    }
    return { start: '00:00', end: '00:00' }
  })
})
</script>

<style lang="scss">
@use '../static/style/variables.scss' as *;
@use '../static/style/mixins.scss' as *;

.table {
  border-collapse: collapse;
  
  th {
    position: relative;
    padding-right: 2px;

    .table__cell {
      margin-right: 0;
    }
  }

  &__scroll {
    max-width: 100%;
    overflow: hidden;
    height: max-content;

    &::-webkit-scrollbar-thumb {
      background: #DDDDDD;   
      border-radius: 20px;
    }

    &::-webkit-scrollbar {
      height: 10px;        
    }
  }

  &__head {
    text-transform: capitalize;
  }

  &__cell {
    height: 30px;
    margin-bottom: 4px;
    font-size: 15px;
    font-weight: 500;
    line-height: 18px;
    width: 94px;
    border-radius: 3px;
    background: #C8C8C8;
    margin-right: 2px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0 5px;

    &-sub {
      background: $bg-color-subdark;
      border: 1px solid #CCCCCC;
    }

    &-dayoff {
      background-color: $th-color;
    }

    &-time {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      font-weight: 500;
    }

    &-icon {
      min-width: 6px;
      width: 6px;
      height: 6px;
    }

    &-body {
      height: 94px;
      background: $bg-color-light;
      justify-content: flex-start;
      margin-bottom: 6px;

      &:has(.table__cell-vacation) {
        .time-slot-wrap {
          display: none;
        }
      }

      &:has(.has-canceled) {
        .table__cell-vacation {
          margin-bottom: -6px;
        }
      }
    }

    &-vacation {
      margin: auto;
    }

    &-warn {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 4px;
      border-radius: 3px;
      background: #FF3B30;
      color: #FFFFFF;
      font-style: normal;
      font-weight: 500;
      font-size: 10px;
      line-height: 12px;
      text-align: left;
      display: flex;
      align-items: center;
      padding-left: 4px;
      padding-right: 7px;
      transition: all .15s ease-out;
      transform: translateY(-86%);
      cursor: default;
    }

    &-inner {
      overflow: hidden;
      border-radius: 3px;
      position: relative;

      &:hover .table__cell-warn {
        transform: translateY(0);
      }
    }
  }
  tbody {
    td {
      .table__cell {
        padding: 14px 3px;
        border: 1px solid #CCCCCC;
        position: relative;
      }

      &.active {
        position: relative;
        .table__cell {
          background: $white;
          border: 1px solid $blue;
          box-shadow: 0 0 0 2px #CBE9FF;
        }
      }

      &.today:not(.active) .table__cell {
        border-color: rgba(51, 51, 51, 1);
      }
    }
  }
}

th.today .table__cell {
  background: #909090;
  color: $white;
  border: 1px solid rgba(51, 51, 51, 1);
}

td.today .table__cell-sub {
  border-color: rgba(51, 51, 51, 1);
}

th.more {
  .table__cell-warn {
    background: #FF6F20;
  }
}

.dayOut {
  .table__cell {
    background: #EEF8FF;
  }
}

.holiday {
  .table__cell {
    background: #FFEAEE;
  }
}

.time-slot {
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 15px;
  text-align: center;
  border: 1px solid rgba(204, 204, 204, 0.5);
  border-radius: 3px;
  background: rgba(255, 255, 255, 1);
  white-space: nowrap;

  &-big {
    width: calc(100% + 9px);
    margin-left: 50%;
    z-index: 99;

  }
}

.time-slot-wrap {
  border: unset;
}

.time-slot-passed {
  .time-slot {
    color: rgba(51, 51, 51, 0.5);
    border-color: rgba(204, 204, 204, 0.5);
  }

  .time-slot-label {
    opacity: .7;
  }
}

.table__cell-body > :last-of-type:not(.more):not(:first-child):not(.time-slot-big):not(:last-of-type) {
  display: none;
}

.table__cell-body:not(:has(.shift)) > .time-slot:nth-child(n+4) {
  display: none;
}

td:has(.time-slot-big) + td .table__cell:not(:has(.time-slot-big))::before {
    content: "";
    display: block;
    font-size: 12px;
    font-weight: 500;
    text-align: center;
    border-radius: 3px;
    line-height: 13px;
    background: transparent;
    white-space: nowrap;
    min-height: 15px;
    margin-bottom: 12px;
}

.table__cell-body > :last-of-type:not(.more):not(:first-child):not(.time-slot-big):not(:last-of-type) {
  display: none;
}

.table__cell-body:has(.modal-wrap):has(.shift):has(.more) > .time-slot:nth-child(-n+3) {
  display: block;
}

td:has(.time-slot-big) + td .table__cell:not(:has(.time-slot-big))[data-count] {
  &:not(:has(.shift)).time-slot:not(:first-of-type) {
    display: none;
  }
  .time-slot + .time-slot {
    display: none;
  }
  .more {
    display: block!important;
  }
}

td:has(.time-slot-big) + td .table__cell:has(.time-slot-big)[data-count] {
  .time-slot + .time-slot:not(.time-slot-big) {
    display: none;
  }

  .more {
    display: block!important;
  }
}

td:has(.time-slot-big):has(.more-visible) .time-slot:not(:last-of-type) + .time-slot:not(:last-of-type) {
  display: none;
}

.time-slot-big {
  order: -1;
  margin-top: 0!important;
  margin-bottom: 12px;
}
</style>
